/**
 * AnagraficaSection — F-002 Phase 4 (IMPL-P4-005).
 *
 * Tab "Anagrafica" del BillingProfileForm:
 *  - RadioGroup PF/PG (controlla la visibilità condizionale dei campi sotto)
 *  - PF: firstName + lastName
 *  - PG: ragioneSociale
 *
 * Usa `useFormContext<BillingProfileUpsertInput>()` per leggere/scrivere i
 * campi, con `Controller` per il RadioGroup (componente Radix non standard
 * HTML — non si registra direttamente con `register`).
 */
import { Controller, useFormContext } from 'react-hook-form';
import type { BillingProfileUpsertInput } from '@app/shared';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export function AnagraficaSection() {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<BillingProfileUpsertInput>();

  const type = watch('type');

  return (
    <div className="space-y-6">
      {/* Tipo soggetto */}
      <div className="space-y-2">
        <Label>Tipo soggetto</Label>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="grid grid-cols-2 gap-2 sm:max-w-md"
            >
              <Label
                htmlFor="type-pf"
                className="flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-accent"
              >
                <RadioGroupItem id="type-pf" value="PF" />
                <span className="text-sm font-medium">Persona Fisica</span>
              </Label>
              <Label
                htmlFor="type-pg"
                className="flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-accent"
              >
                <RadioGroupItem id="type-pg" value="PG" />
                <span className="text-sm font-medium">Persona Giuridica</span>
              </Label>
            </RadioGroup>
          )}
        />
        {errors.type ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {errors.type.message as string}
          </p>
        ) : null}
      </div>

      {/* Campi PF */}
      {type === 'PF' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">Nome</Label>
            <Input
              id="firstName"
              autoComplete="given-name"
              aria-invalid={Boolean('firstName' in errors && errors.firstName)}
              {...register('firstName' as const)}
            />
            {'firstName' in errors && errors.firstName ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {errors.firstName.message as string}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Cognome</Label>
            <Input
              id="lastName"
              autoComplete="family-name"
              aria-invalid={Boolean('lastName' in errors && errors.lastName)}
              {...register('lastName' as const)}
            />
            {'lastName' in errors && errors.lastName ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {errors.lastName.message as string}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Campi PG */}
      {type === 'PG' ? (
        <div className="space-y-2">
          <Label htmlFor="ragioneSociale">Ragione sociale</Label>
          <Input
            id="ragioneSociale"
            autoComplete="organization"
            aria-invalid={Boolean('ragioneSociale' in errors && errors.ragioneSociale)}
            {...register('ragioneSociale' as const)}
          />
          {'ragioneSociale' in errors && errors.ragioneSociale ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {errors.ragioneSociale.message as string}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
